<template>
  <div
    class="pt-3 bg-white multisteps-form__panel border-radius-xl"
    data-animation="FadeIn"
  >
    <h5 class="font-weight-bolder text-shareguard">Upload a file</h5>
    <div class="multisteps-form__content">
      <div class="mt-3 row">
        <div class="col-12">
          <div
            id="productImg"
            action="#"
            class="form-control border dropzone shareguard"
            :class="{ 'dropzone-disabled': isLoadingUpload }"
            :disabled="isLoadingUpload"
            multiple
          ></div>
        </div>
      </div>

      <div v-if="!isEncryptionInitialized" class="alert alert-warning mt-3">
        Encryption is not initialized. Please set up your encryption keys before uploading files.
      </div>

      <!-- <div v-if="isLoadingUpload" class="alert alert-info mt-3">
        <i class="fas fa-upload me-2"></i>
        Upload in progress. Please wait for current uploads to complete before adding new files.
      </div> -->

      <div v-if="showProgress && files.length" class="mt-3">
        <!-- Queue Summary -->
        <!-- <div v-if="hasQueuedFiles" class="warning warning-info mb-3" style="font-size: 0.9rem;">
          <i class="fas fa-info-circle me-2"></i>
          <strong>{{ queuedFilesCount }}</strong> file(s) queued for upload. 
        </div> -->
        
        <div v-for="file in files" :key="file.name" class="mb-2">
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <span class="text-sm">{{ file.name }}</span>
              <span v-if="file.isQueued" class="badge bg-secondary ms-2 mb-1" style="font-size: 0.6rem;">
                <i class="fas fa-clock me-1"></i>Queued
              </span>
              <span v-else-if="file.isProcessing" class="badge bg-info ms-2" style="font-size: 0.6rem;">
                <i class="fas fa-spinner fa-spin me-1"></i>Processing
              </span>
            </div>
            <div class="d-flex align-items-center">
              <span class="text-sm me-2">{{ file.progress ? file.progress.toFixed(1) : 0 }}%</span>
              <button
                v-if="getOperationIdByFile(file)"
                class="btn btn-outline-danger btn-sm rounded-circle d-flex align-items-center justify-content-center"
                style="width: 24px; height: 24px; padding: 0;"
                title="Cancel upload"
                @click="cancelFileUpload(file)"
              >
                <span style="font-size: 1rem; line-height: 1;">&times;</span>
              </button>
            </div>
          </div>
          <div class="progress">
            <div 
              :class="getProgressBarClass(file)"
              role="progressbar" 
              :style="{ width: (file.progress || 0) + '%' }"
              :aria-valuenow="file.progress || 0" 
              aria-valuemin="0" 
              aria-valuemax="100"
            ></div>
            <div v-if="file.progress && file.progress < 5 && !file.isQueued" class="progress-bar-striped progress-bar-animated" style="width: 100%; background: rgba(255,255,255,0.3);"></div>
            <div v-if="file.isQueued" class="progress-bar-striped progress-bar-animated" style="width: 100%; background: rgba(108, 117, 125, 0.3);"></div>
          </div>
          <div v-if="file.status && file.status !== 'Ready to upload'" class="text-xs text-muted mt-1">
            {{ file.status }}
          </div>
        </div>
      </div>

      <div class="mt-4 button-row d-flex col-12">
        <material-button
          type="button"
          color="shareguard"
          variant="fill"
          class="mb-0 ms-auto js-btn-next"
          title="Submit"
          :disabled="isLoadingUpload || !isEncryptionInitialized || !files.length"
          @click="submit"
        >
          {{ isLoadingUpload ? `Uploading ${files.length} file${files.length !== 1 ? 's' : ''}...` : "Upload Files" }}
        </material-button>
      </div>
    </div>
  </div>
</template>

<script>
import Dropzone from "dropzone";
import MaterialButton from "@/components/ui/MaterialButton.vue";
import { mapGetters, mapActions } from "vuex";
import { cryptoService } from "@/services/crypto.service";

export default {
  name: "Media",
  components: {
    MaterialButton,
  },
  data() {
    return {
      dropzoneInstance: null,
      files: [],
      uploadProgress: 0,
      currentFile: null,
      showProgress: false
    };
  },
  computed: {
    ...mapGetters("files", ["isLoadingUpload", "getQueueStats", "getOperationIdByFile"]),
    ...mapGetters("encryption", ["isEncryptionInitialized"]),
    hasQueuedFiles() {
      return this.files.some(file => file.isQueued);
    },
    queuedFilesCount() {
      return this.files.filter(file => file.isQueued).length;
    },
    maxConcurrentUploads() {
      return this.getQueueStats?.maxConcurrent || 1;
    }
  },
  watch: {
    isLoadingUpload(newValue) {
      if (this.dropzoneInstance) {
        if (newValue) {
          // Disable dropzone when upload starts
          this.dropzoneInstance.disable();
          // Update the message to indicate uploads are in progress
          this.updateDropzoneMessage("Upload in progress. Please wait...");
        } else {
          // Enable dropzone when upload finishes
          this.dropzoneInstance.enable();
          // Restore the default message
          this.updateDropzoneMessage("Drop files here or click to upload");
        }
      }
    }
  },
  mounted() {
    this.dropzoneInstance = new Dropzone("#productImg", {
      addRemoveLinks: true,
      dictCancelUpload: "Cancel",
      dictCancelUploadConfirmation: "Are you sure you want to cancel this upload?",
      autoProcessQueue: false,
      maxFiles: 10,
      dictDefaultMessage: "Drop files here or click to upload",
      dictFallbackMessage: "Your browser does not support drag'n'drop file uploads.",
      dictFileTooBig: "File is too big ({{filesize}}MB). Max filesize: {{maxFilesize}}MB.",
      dictInvalidFileType: "You can't upload files of this type.",
      dictResponseError: "Server responded with {{statusCode}} code.",
      dictRemoveFile: "Remove",
      dictMaxFilesExceeded: "You can not upload any more files.",
      accept: (file, done) => {
        // Prevent adding files if upload is in progress
        if (this.isLoadingUpload) {
          done("Upload in progress. Please wait for current uploads to complete.");
          return;
        }
        
        const existingFile = this.files.find(f => 
          f.name === file.name && f.size === file.size
        );
        
        if (existingFile) {
          done("File already added");
          return;
        }
        
        done();
      }
    });

    this.dropzoneInstance.on("addedfile", (file) => {
      // Forcefully clear any previous status or error properties
      file.status = 'Ready to upload';
      file.progress = 0;
      file.isQueued = false;
      file.isProcessing = false;
      file.queuePosition = null;
      this.files.push(file);
    });
    
    this.dropzoneInstance.on("removedfile", (file) => {
      // Check if this file has an active upload operation and abort it
      // Only abort if the file is still queued or processing (not completed)
      const operationId = this.getOperationIdByFile(file);
      if (operationId && (file.isQueued || file.isProcessing)) {
        console.log(`[Media] Aborting upload for file: ${file.name}, operationId: ${operationId}`);
        this.abortSpecificUploadAction(operationId);
      }
      
      this.files = this.files.filter(f => f !== file);
      if (this.currentFile === file) {
        this.currentFile = null;
        this.uploadProgress = 0;
      }
      // Clear queue state for removed file
      file.isQueued = false;
      file.isProcessing = false;
      file.queuePosition = null;
    });
  },
  beforeUnmount() {
    if (this.dropzoneInstance) {
      this.dropzoneInstance.destroy();
    }
  },
  methods: {
    ...mapActions("files", ["uploadFileAction", "abortSpecificUploadAction"]),
    updateDropzoneMessage(message) {
      if (this.dropzoneInstance) {
        // Find the dropzone message element and update it
        const messageElement = this.dropzoneInstance.element.querySelector('.dz-message');
        if (messageElement) {
          messageElement.textContent = message;
        }
      }
    },
    getProgressBarClass(file) {
      if (file.isQueued) {
        return 'progress-bar bg-secondary';
      } else if (file.isProcessing) {
        return 'progress-bar bg-info';
      } else {
        return 'progress-bar bg-success';
      }
    },
    cancelFileUpload(file) {
      const operationId = this.getOperationIdByFile(file);
      if (operationId) {
        console.log(`[Media] Cancelling upload for file: ${file.name}, operationId: ${operationId}`);
        this.abortSpecificUploadAction(operationId);
        // Remove the file from dropzone
        this.dropzoneInstance.removeFile(file);
      }
      // If all files are removed, clear the files array
      if (this.files.length === 1 && this.files[0] === file) {
        this.files = [];
      }
    },
    async submit() {
      if (!this.files.length) {
        console.error("No files to upload.");
        return;
      }

      if (!this.isEncryptionInitialized || !cryptoService.areKeysReady()) {
        this.$store.dispatch("snackbar/showSnackbar", { 
          title: "Encryption not initialized. Please initialize encryption before uploading files.", 
          color: "danger" 
        }, { root: true });
        return;
      }

      this.showProgress = true;

      try {
        // Create a copy of files array to avoid issues with concurrent modifications
        const filesToUpload = [...this.files];
        
        await Promise.all(filesToUpload.map(async (file) => {
          try {
            // Check if file is still in the current files array (not cancelled)
            if (!this.files.includes(file)) {
              console.log(`[Media] Skipping cancelled file: ${file.name}`);
              return;
            }
            
            file.status = 'Starting upload...';
            this.files = [...this.files]; // Force reactivity update
            
            await this.uploadFileAction({
              file,
              onProgress: (progress, progressInfo) => {
                // Check if file is still in the current files array (not cancelled)
                if (!this.files.includes(file)) {
                  console.log(`[Media] Skipping progress update for cancelled file: ${file.name}`);
                  return;
                }
                
                file.progress = progress;
                file.status = progressInfo?.status || 'Uploading...';
                file.isQueued = progressInfo?.isQueued || false;
                file.isProcessing = progressInfo?.isProcessing || false;
                file.queuePosition = progressInfo?.queuePosition || null;
                this.uploadProgress = progress;
                // Force Vue to update the array in case reactivity is missed
                this.files = [...this.files];
              }
            });
            
            // Check if file is still in the current files array before removing
            if (this.files.includes(file)) {
              this.dropzoneInstance.removeFile(file);
            }
            file.progress = 0;
          } catch (error) {
            // Do not remove the file from dropzone if upload failed
            console.error('Upload error for file:', file.name, error);
          }
        }));
        this.files = [];
      } catch (error) {
        console.error('Upload error:', error);
        
      } finally {
        this.showProgress = false;
      }
    }
  }
};
</script>

<style>
.dropzone .dz-preview {
  position: relative;
  margin: 0;
}

.dropzone .dz-preview .dz-image {
  border-radius: 4px;
  overflow: hidden;
  width: 120px;  /* Default size for larger screens */
  height: 120px;
}

/* Make thumbnails smaller on mobile screens */
@media (max-width: 768px) {
  .dz-preview .dz-image-preview {
    margin: 8px !important;
  }
  
  .dropzone .dz-preview .dz-details {
    padding: 0 !important;
  }
  .dropzone .dz-preview .dz-image {
    width: 80px !important;
    height: 80px !important;
  }
  
  .dropzone .dz-preview .dz-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.dropzone .dz-preview .dz-preview .dz-remove {
  /* Remove all custom styles here */
}

.dropzone .dz-preview .dz-preview .dz-remove i {
  /* Remove this too */
}

.dz-progress {
  display: none;
}

/* Disabled dropzone styles */
.dropzone-disabled {
  opacity: 0.6;
  pointer-events: none;
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

.dropzone-disabled .dz-message {
  color: #6c757d;
}

.dropzone-disabled .dz-preview {
  opacity: 0.6;
}
</style>
